import { Comparison } from '@/features/api';
import {
  DateProperty,
  DropdownProperty,
  NumberProperty,
  PropertyType,
  TextProperty,
} from '@/legacy/shared/components/SearchFilter/models/Property';

import { ReceivablesType } from '../models/receivablesTablePage';
import ReceivablesTableRowData from '../models/receivablesTableRow';
import ReceivablesTableSearchQuery from '../models/receivablesTableSearchQuery';

import ReceivablesTableQuery, { ReceivablesTableQueryType } from './ReceivablesTable.queries';

interface ReceivablesTableProperties {
  readonly requestStatus: DropdownProperty;
  readonly amount: NumberProperty;
  readonly createdDate: DateProperty;
  readonly scheduledDate: DateProperty;
  readonly withdrawnOn: DateProperty;
  readonly creditedOn: DateProperty;
  readonly creditCardUsed: DropdownProperty;
  readonly bank: DropdownProperty;
  readonly memo: TextProperty;
  readonly initiatedBy: DropdownProperty;
  readonly recurring: DropdownProperty;
  readonly padUsed: DropdownProperty;
  readonly imported: DropdownProperty;
}

interface ReceivablesTableState {
  readonly properties: ReceivablesTableProperties;
  readonly propertiesOrder: Array<keyof ReceivablesTableProperties>;
  readonly query: ReceivablesTableSearchQuery;
  readonly totalNumberOfRecords: number;
  readonly grandTotalNumberOfRecords: number;
  readonly rows: Array<ReceivablesTableRowData>;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly filterStatus: 'not loaded' | 'loading' | 'loaded';
  readonly queries: Array<ReceivablesTableQuery>;
}

interface ReceivablesTableInitialStateOptions {
  readonly receivablesType: ReceivablesType;
  readonly payeeId?: string;
}

const pendingReceivablesStatusOptions = [
  { value: 'AwaitingRequestAcceptance', label: 'Awaiting Request Acceptance' },
  { value: 'PaymentScheduled', label: 'Payment Scheduled' },
  { value: 'InClearing', label: 'In Clearing' },
];

const completedReceivablesStatusOptions = [
  { value: 'DebitFailed', label: 'Debit Failed' },
  { value: 'CreditFailed', label: 'Credit Failed' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Deleted', label: 'Deleted' },
  { value: 'Rejected', label: 'Rejected' },
];

const getInitialState = ({
  receivablesType,
  payeeId = '',
}: ReceivablesTableInitialStateOptions): ReceivablesTableState => {
  const initialQuery: ReceivablesTableSearchQuery = {
    searchQuery: '',
    filters: [],
    sortDirections: {
      contact: false,
      status: false,
      requestedDebitDate: false,
      amount: false,
    },
    sortOrder: [],
    pageIndex: 0,
    pageSize: 10,
    receivablesType,
    payeeId,
  };

  let statusOptions = [];

  switch (receivablesType) {
    case 'pending':
      statusOptions = pendingReceivablesStatusOptions;
      break;
    case 'completed':
      statusOptions = completedReceivablesStatusOptions;
      break;
    default:
      statusOptions = [...pendingReceivablesStatusOptions, ...completedReceivablesStatusOptions];
      break;
  }

  return {
    properties: {
      requestStatus: {
        name: 'Status',
        label: 'Request Status',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: statusOptions,
      },
      amount: {
        name: 'Amount',
        label: 'Amount',
        type: PropertyType.Number,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
          { value: Comparison.LessThan, label: 'less than' },
          { value: Comparison.GreaterThan, label: 'greater than' },
          { value: Comparison.LessOrEqual, label: 'less or equal' },
          { value: Comparison.GreaterOrEqual, label: 'greater or equal' },
        ],
      },
      createdDate: {
        name: 'CreatedDate',
        label: 'Created Date',
        type: PropertyType.Date,
      },
      scheduledDate: {
        name: 'DebitDate',
        label: 'Requested Debit Date',
        type: PropertyType.Date,
      },
      withdrawnOn: {
        name: 'WithdrawnOn',
        label: 'Withdrawn On',
        type: PropertyType.Date,
      },
      creditedOn: {
        name: 'CreditedOn',
        label: 'Credited On',
        type: PropertyType.Date,
      },
      creditCardUsed: {
        name: 'CreditCardUsed',
        label: 'Credit Card Used?',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ],
      },
      bank: {
        name: 'Bank',
        label: 'Bank',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: [],
        loading: true,
      },
      memo: {
        name: 'Memo',
        label: 'Memo',
        type: PropertyType.Text,
        relations: [{ value: Comparison.Contains, label: 'contains' }],
      },
      initiatedBy: {
        name: 'InitiatedBy',
        label: 'Initiated By',
        type: PropertyType.Dropdown,
        relations: [
          { value: Comparison.Equals, label: 'equals' },
          { value: Comparison.NotEquals, label: 'not equal to' },
        ],
        values: [],
        loading: true,
      },
      recurring: {
        name: 'Recurring',
        label: 'Recurring',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ],
      },
      padUsed: {
        name: 'PadUsed',
        label: 'PAD Used',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ],
      },
      imported: {
        name: 'Imported',
        label: 'Imported',
        type: PropertyType.Dropdown,
        relations: [{ value: Comparison.Equals, label: 'equals' }],
        values: [
          { value: '1', label: 'Yes' },
          { value: '0', label: 'No' },
        ],
      },
    },
    propertiesOrder: [
      'requestStatus',
      'amount',
      'createdDate',
      'scheduledDate',
      'withdrawnOn',
      'creditedOn',
      'creditCardUsed',
      'bank',
      'memo',
      'initiatedBy',
      'recurring',
      'padUsed',
      'imported',
    ],
    query: initialQuery,
    rows: [],
    totalNumberOfRecords: 0,
    grandTotalNumberOfRecords: null,
    isLoading: true,
    isError: false,
    filterStatus: 'not loaded',
    queries: [
      {
        type: ReceivablesTableQueryType.GetPage,
        query: initialQuery,
      },
    ],
  };
};

export { ReceivablesTableProperties, getInitialState };
export default ReceivablesTableState;
